import React from 'react';
import { Tag, Typography } from 'antd';

import { feeOf, feeRateText, ratePercent } from '../domain/feeRate.js';
import { formatMoney } from '../format';

const { Text } = Typography;

const dash = () => <Text type="secondary">—</Text>;

/**
 * A transaction's fee, read-only: the LKR figure and, beside it, the % it is
 * charged (domain/feeRate.js feeRateText) - one cell for every screen that
 * shows a transaction, whichever direction its fee is. It reads `feeAmount`
 * and the fee the server sent: an order's `fees` (IP-203: its Apply fees
 * terms) or a posted transaction's `feeRate` (domain/feeRate.js feeOf).
 *
 *   1,500.00 (1.5%)                    the fee, and its rate
 *   0.00 (Fees not applied - fund 1%)  Apply fees unticked at review
 *   1,500.00                           the fee alone, in a table with its own Fee % column (showRate false)
 *   1.5% of the amount                 no fee figure yet (no price to price at), but the rate is known
 *   —                                  neither
 *
 * @param {{ row: { feeAmount?: string|null, fees?: object|null, feeRate?: object|null }, showRate?: boolean }} props
 */
const FeeCell = ({ row, showRate = true }) => {
    const fee = row?.feeAmount ?? null;
    const shown = feeOf(row);
    const rate = feeRateText(shown);
    if (fee === null && (rate === null || !showRate)) return dash();
    // A subscription's rate is of its amount - said right after its own rate; an exit rate already says what it is charged on.
    if (fee === null) return <Text type="secondary">{feeRateText(shown, { ofAmount: shown?.kind === 'ENTRY' })}</Text>;
    return (
        <span className="order-figure-cell">
            {formatMoney(fee)}
            {showRate && rate && <Text type="secondary" className="order-fee-rate">{'(' + rate + ')'}</Text>}
        </span>
    );
};

/**
 * A transaction's fee %, in its own table column: the rate, what an exit
 * rate is charged on, and a tag when the fee was not applied (IP-203: Apply
 * fees unticked at review, with the fund's % under it), when the % is still
 * provisional, or when an older subscription carried its own rate set at
 * review (IP-139). — when no fee % applies (a transfer, an upload, a leg
 * whose fund charges no such fee).
 *
 *   1.12%
 *   2%   on units held under 12 months
 *   [Not applied]   fund 1.12%
 *   1.12%  [Provisional]
 *
 * @param {{ row: { fees?: object|null, feeRate?: object|null } }} props
 */
export const FeeRateCell = ({ row }) => {
    const fee = feeOf(row);
    if (!fee) return dash();
    if (!fee.applied) {
        return (
            <span className="order-fee-rate-cell">
                <Tag className="order-fee-rate-tag">Not applied</Tag>
                {fee.fundRate && <div><Text type="secondary" className="order-fee-rate">{'fund ' + ratePercent(fee.fundRate)}</Text></div>}
            </span>
        );
    }
    const rate = ratePercent(fee.rate ?? null);
    if (!rate) return dash();
    return (
        <span className="order-fee-rate-cell">
            {rate}
            {fee.provisional && <Tag className="order-fee-rate-tag">Provisional</Tag>}
            {fee.overridden && <Tag className="order-fee-rate-tag" color="gold">Set at review</Tag>}
            {fee.overridden && (
                <div><Text type="secondary" className="order-fee-rate">{'fund ' + (ratePercent(fee.fundRate ?? null) ?? '0%')}</Text></div>
            )}
            {fee.kind === 'EXIT' && fee.holdingMonths && (
                <div><Text type="secondary" className="order-fee-rate">{'on units held under ' + fee.holdingMonths + ' months'}</Text></div>
            )}
        </span>
    );
};

export default FeeCell;
